import { RpcFunction } from '@examdojo/supabase';

export type ClassInsightsRpcRow = RpcFunction<'get_class_insights', 'classroom'>['Returns'][number];

export interface ClassInsightsGradeDistributionUIModel {
  grade: string;
  gradeDisplayName: string;
  lowerBoundPercentage: number;
  studentCount: number;
}

export interface ClassInsightsWeakestSpotUIModel {
  topicLevel02Id: number;
  topicCode: string;
  topicName: string;
  markImpact: number;
  avgAccuracy: number;
}

export interface ClassInsightsTopicUIModel {
  topicLevel02Id: number;
  topicCode: string;
  topicName: string;
  topicGroupName: string;
  avgMark: number | null;
  examWeight: number;
  markImpact: number | null;
  avgAccuracy: number | null;
  lower: number | null;
  upper: number | null;
  isAssessed: boolean;
}

export type ClassInsightsGradeDistributionStoreModel = ClassInsightsGradeDistributionUIModel;
export type ClassInsightsWeakestSpotStoreModel = ClassInsightsWeakestSpotUIModel;
export type ClassInsightsTopicStoreModel = ClassInsightsTopicUIModel;

export interface ClassInsightsStoreModel {
  classId: string;
  studentCount: number;
  studentsWithData: number;
  courseCoverageCompletedTopics: number;
  courseCoverageTotalTopics: number;
  gradeDistribution: ClassInsightsGradeDistributionStoreModel[];
  weakestSpots: ClassInsightsWeakestSpotStoreModel[];
  topics: ClassInsightsTopicStoreModel[];
}

export interface ClassInsightsUIModel extends ClassInsightsStoreModel {
  courseCoveragePercent: number;
}

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const toBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  return fallback;
};

const toString = (value: unknown, fallback = ''): string => {
  return typeof value === 'string' ? value : fallback;
};

const toObjectArray = (value: unknown): Array<Record<string, unknown>> => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (item): item is Record<string, unknown> => typeof item === 'object' && item !== null && !Array.isArray(item),
  );
};

export const createEmptyClassInsightsStoreModel = (classId: string): ClassInsightsStoreModel => ({
  classId,
  studentCount: 0,
  studentsWithData: 0,
  courseCoverageCompletedTopics: 0,
  courseCoverageTotalTopics: 0,
  gradeDistribution: [],
  weakestSpots: [],
  topics: [],
});

export const mapClassInsightsRpcRowToStoreModel = (
  row: ClassInsightsRpcRow | null,
  classId: string,
): ClassInsightsStoreModel => {
  if (!row) {
    return createEmptyClassInsightsStoreModel(classId);
  }

  return {
    classId: toString(row.class_id, classId),
    studentCount: toNumber(row.student_count),
    studentsWithData: toNumber(row.students_with_data),
    courseCoverageCompletedTopics: toNumber(row.course_coverage_completed_topics),
    courseCoverageTotalTopics: toNumber(row.course_coverage_total_topics),
    gradeDistribution: toObjectArray(row.grade_distribution).map((entry) => ({
      grade: toString(entry['grade']),
      gradeDisplayName: toString(entry['grade_display_name']),
      lowerBoundPercentage: toNumber(entry['lower_bound_percentage']),
      studentCount: toNumber(entry['student_count']),
    })),
    weakestSpots: toObjectArray(row.weakest_spots).map((entry) => ({
      topicLevel02Id: toNumber(entry['topic_level_02_id']),
      topicCode: toString(entry['topic_code']),
      topicName: toString(entry['topic_name']),
      markImpact: toNumber(entry['mark_impact']),
      avgAccuracy: toNumber(entry['avg_accuracy']),
    })),
    topics: toObjectArray(row.topics).map((entry) => ({
      topicLevel02Id: toNumber(entry['topic_level_02_id']),
      topicCode: toString(entry['topic_code']),
      topicName: toString(entry['topic_name']),
      topicGroupName: toString(entry['topic_group_name']),
      avgMark: toNullableNumber(entry['avg_mark']),
      examWeight: toNumber(entry['exam_weight']),
      markImpact: toNullableNumber(entry['mark_impact']),
      avgAccuracy: toNullableNumber(entry['avg_accuracy']),
      lower: toNullableNumber(entry['lower']),
      upper: toNullableNumber(entry['upper']),
      isAssessed: toBoolean(entry['is_assessed']),
    })),
  };
};
